import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import * as BsIcons from 'react-icons/bs';
import SafeIcon from '../../common/SafeIcon';

const { FiCpu, FiSettings, FiShield, FiZap, FiGlobe, FiHeadphones, FiEdit3, FiCheckCircle, FiDownload, FiClock, FiUsers, FiBookOpen } = FiIcons;
const { BsSpeedometer, BsWaveform } = BsIcons;

const TechFeatures = () => {
  // Core tech features
  const coreFeatures = [
    {
      icon: FiEdit3,
      title: "Author Voice DNA",
      description: "Stylometric analysis across seven dimensions learns your sentence rhythm, vocabulary, and tone, then writes new prose that matches."
    },
    {
      icon: FiZap,
      title: "Writes In Your Voice",
      description: "Continue, rewrite, and outline with AI grounded in your own style - not a generic, blended model that sounds like everyone else."
    },
    {
      icon: FiCheckCircle,
      title: "Live KDP Preflight",
      description: "Trim size, margins, headings, table of contents, and metadata are checked as you write, with one-click fixes before you publish."
    },
    {
      icon: FiShield,
      title: "Your Writing Stays Private",
      description: "End-to-end encryption and secure storage. Your manuscript and your voice model are never shared or used to train other models."
    }
  ];

  // Platform capabilities
  const platformCapabilities = [
    {
      icon: FiDownload,
      title: "One-Click Export",
      description: "Export clean EPUB, PDF, DOCX, and KDP-ready files - formatted to spec, ready to upload."
    },
    {
      icon: FiClock,
      title: "Version History",
      description: "Every draft is saved automatically. Roll back any chapter to an earlier version whenever you need to."
    },
    {
      icon: FiUsers,
      title: "Collaboration",
      description: "Invite editors and co-authors with comments and tracked changes, all in one shared manuscript."
    },
    {
      icon: FiBookOpen,
      title: "Distraction-Free Editor",
      description: "Organize by chapters and scenes, set word-count goals, and write in a clean focus mode built for long-form."
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 to-primary-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            className="text-4xl md:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Powered by{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400">
              Author Voice DNA
            </span>
          </motion.h2>
          <motion.p
            className="text-xl opacity-90 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            A writing studio built to sound like you - and keep your book publish-ready at every step
          </motion.p>
        </div>

        {/* Core Features */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {coreFeatures.map((feature, index) => (
            <motion.div
              key={index}
              className="relative p-6 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="absolute top-0 right-0 w-24 h-24 -mt-10 -mr-10 opacity-10">
                <SafeIcon icon={feature.icon} className="w-full h-full text-primary-400" />
              </div>
              <div className="p-2 mb-4 w-14 h-14 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center shadow-lg">
                <SafeIcon icon={feature.icon} className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
              <p className="text-gray-300 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Technology Visualization */}
        <motion.div
          className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl overflow-hidden shadow-2xl mb-16 p-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="absolute inset-0 opacity-30 overflow-hidden">
            {/* Technology Background Pattern */}
            <div className="absolute inset-0 grid grid-cols-12 gap-2">
              {[...Array(60)].map((_, i) => (
                <div
                  key={i}
                  className="col-span-1 h-4 rounded-full bg-primary-500/20"
                  style={{
                    animationDelay: `${i * 0.1}s`,
                    animationDuration: '3s',
                    animationName: 'pulse',
                    animationIterationCount: 'infinite',
                    animationTimingFunction: 'ease-in-out'
                  }}
                />
              ))}
            </div>
            
            {/* Flowing Data Visualization */}
            <div className="absolute top-1/2 left-0 right-0 h-12 overflow-hidden">
              <div
                className="h-full flex items-center"
                style={{
                  animation: 'waveFlow 15s linear infinite',
                  width: '300%'
                }}
              >
                {[...Array(40)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 bg-gradient-to-t from-primary-400 to-secondary-400 rounded-full"
                    style={{
                      height: `${Math.sin(i * 0.5) * 50 + 50}%`,
                      opacity: i % 2 === 0 ? 0.8 : 0.5,
                      animationDuration: `${0.8 + (i * 0.05)}s`
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <div className="relative z-10 grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">How Author Voice DNA Works</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Import a draft or start fresh, then paste a writing sample - ManuscriptSmith studies its sentence length, rhythm, punctuation habits, word choice, and tone across seven dimensions to build your private style profile. From there, every rewrite, continuation, and outline is generated to match that profile, and Live KDP Preflight keeps the manuscript export-ready the whole way through.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                  <span className="text-gray-200">Import or start fresh - DOCX, EPUB, TXT, PDF, or a blank page</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-secondary-500 rounded-full"></div>
                  <span className="text-gray-200">Your own private style profile, never shared or used to train other models</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-200">Every suggestion matched to your voice, chapter after chapter</span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="relative w-64 h-64">
                <div className="absolute inset-0 rounded-full border-4 border-dashed border-primary-400/30 animate-spin-slow"></div>
                <div className="absolute inset-8 rounded-full border-4 border-dashed border-secondary-400/40 animate-spin-slow" style={{ animationDirection: 'reverse', animationDuration: '30s' }}></div>
                <div className="absolute inset-16 rounded-full border-4 border-dashed border-primary-400/50 animate-spin-slow" style={{ animationDuration: '20s' }}></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 shadow-lg flex items-center justify-center">
                    <SafeIcon icon={FiEdit3} className="w-12 h-12 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Platform Capabilities */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {platformCapabilities.map((capability, index) => (
            <motion.div
              key={index}
              className="p-6 bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-xl border border-gray-700 hover:border-primary-500/50 transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="p-2 mb-4 w-14 h-14 rounded-full bg-gray-800 flex items-center justify-center shadow-lg border border-gray-700">
                <SafeIcon icon={capability.icon} className="w-7 h-7 text-primary-400" />
              </div>
              <h3 className="text-xl font-bold mb-3">{capability.title}</h3>
              <p className="text-gray-300 leading-relaxed">{capability.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Voice DNA CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Link
            to="/voice-dna"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-600 transition-all transform hover:scale-105 shadow-lg"
          >
            <SafeIcon icon={FiEdit3} className="w-5 h-5 mr-2" />
            See Author Voice DNA in action
          </Link>
          <p className="text-sm text-gray-300 mt-3">Live demo • No signup required</p>
        </motion.div>
      </div>
    </section>
  );
};

export default TechFeatures;